import { inject, Injectable, OnDestroy } from "@angular/core";
import { Socket } from "ngx-socket-io";
import { Observable, Subject, Subscription } from "rxjs";
import { AuthService } from "../../auth/services/auth.service";
import { LiveProductionSnapshotPayload } from "../models/live-production.model";

/**
 * Canal WebSocket dédié à la supervision atelier live. Reçoit les
 * snapshots agrégés (`snapshot` event) poussés par le gateway back
 * `LiveProductionGateway` (`/ws/live-production`).
 *
 * Remplace le polling REST `/live-production/snapshot` qui faisait
 * clignoter la barre de chargement toutes les 5 s.
 *
 * Mutualisation : `connect()` / `disconnect()` utilisent un compteur de
 * souscriptions, identique au pattern de `LiveProductionService` —
 * widget compact + page détachée peuvent souscrire en parallèle sans
 * démultiplier les connexions WS.
 */
@Injectable({ providedIn: "root" })
export class LiveProductionSocketService implements OnDestroy {
  private authService = inject(AuthService);

  private socket: Socket | null = null;
  private snapshotSubj = new Subject<LiveProductionSnapshotPayload>();
  /** Stream des snapshots reçus du serveur (push toutes les 5 s). */
  readonly snapshot$: Observable<LiveProductionSnapshotPayload> =
    this.snapshotSubj.asObservable();

  private snapshotSub: Subscription | null = null;
  private subscriberCount = 0;

  /**
   * Branche la socket et émet `subscribe` au gateway. Le gateway répond
   * immédiatement par un 1ᵉʳ snapshot (évite la latence de bootstrap).
   * Idempotent via subscriberCount.
   */
  connect(): void {
    this.subscriberCount++;
    if (this.socket) return;

    this.socket = this.buildSocket();
    this.snapshotSub = this.socket
      .fromEvent<LiveProductionSnapshotPayload>("snapshot")
      .subscribe((payload) => this.snapshotSubj.next(payload));
    this.socket.connect();
    this.socket.emit("subscribe");
  }

  /** Décrémente le compteur. Coupe la connexion à zéro souscripteur. */
  disconnect(): void {
    this.subscriberCount = Math.max(0, this.subscriberCount - 1);
    if (this.subscriberCount > 0) return;

    if (this.socket) {
      try {
        this.socket.emit("unsubscribe");
      } catch {
        /* socket déjà fermée — on ignore */
      }
      this.snapshotSub?.unsubscribe();
      this.snapshotSub = null;
      this.socket.disconnect();
      this.socket = null;
    }
  }

  private buildSocket(): Socket {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}`;
    return new Socket({
      url,
      options: {
        path: "/ws/live-production",
        query: { token: this.authService.tokenSignal() },
        transports: ["websocket"],
        autoConnect: false,
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriberCount = 0;
    this.snapshotSub?.unsubscribe();
    this.socket?.disconnect();
    this.socket = null;
  }
}
